import React, { useCallback, useEffect, useState } from "react";
import api from "../../api/client";
import { useAuth } from "../Data/AuthContext";
import Dialog from "../common/Dialog";

const BLOOD_TYPES = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];
const PAGE_SIZE = 10;

function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

const EMPTY_FORM = {
  firstname: "",
  lastname: "",
  email: "",
  bloodType: "O+",
  password: "",
  confirmPassword: "",
};

export default function UsersPage() {
  const { user: currentUser } = useAuth();

  const [users, setUsers] = useState([]);
  const [status, setStatus] = useState("loading"); // loading | ready | error
  const [loadError, setLoadError] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const [showAddForm, setShowAddForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);

  // Editing an existing account. Deliberately no password field: the backend
  // accepts one, but an admin typing a raw password into a list screen is a
  // worse flow than the account holder using the existing reset link.
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({
    firstname: "", lastname: "", bloodType: "", role: "user",
  });
  const [editError, setEditError] = useState("");
  const [editSaving, setEditSaving] = useState(false);

  const [pendingDelete, setPendingDelete] = useState(null);
  const [banner, setBanner] = useState(null); // {kind, text}

  const load = useCallback(async () => {
    setStatus("loading");
    try {
      const res = await api.get("/users/users");
      // The controller returns { users: [...] }; tolerate a bare array too.
      const list = res.data?.users || res.data?.data || (Array.isArray(res.data) ? res.data : []);
      setUsers(list);
      setStatus("ready");
    } catch (err) {
      setLoadError(
        err.response?.data?.message || "We couldn't load the user list."
      );
      setStatus("error");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const term = search.trim().toLowerCase();
  const filtered = users.filter(
    (u) =>
      !term ||
      `${u.firstname} ${u.lastname}`.toLowerCase().includes(term) ||
      (u.email || "").toLowerCase().includes(term) ||
      (u.role || "").toLowerCase().includes(term)
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const visible = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  async function handleAddSubmit(e) {
    e.preventDefault();
    setFormError("");

    if (!form.firstname.trim() || !form.lastname.trim() || !form.email.trim()) {
      setFormError("First name, last name and email are all required.");
      return;
    }
    if (form.password.length < 8) {
      setFormError("Password must be at least 8 characters.");
      return;
    }
    if (form.password !== form.confirmPassword) {
      setFormError("The two passwords do not match.");
      return;
    }

    setSaving(true);
    try {
      // Registration is the only account-creation path the API exposes, and
      // it always assigns the "user" role. Promotion is a separate step.
      await api.post("/users/register", {
        firstname: form.firstname.trim(),
        lastname: form.lastname.trim(),
        email: form.email.trim(),
        bloodType: form.bloodType,
        password: form.password,
        confirmPassword: form.confirmPassword,
      });
      setBanner({ kind: "success", text: `Account created for ${form.email.trim()}.` });
      setForm(EMPTY_FORM);
      setShowAddForm(false);
      await load();
    } catch (err) {
      setFormError(
        err.response?.data?.message || "Could not create that account."
      );
    } finally {
      setSaving(false);
    }
  }

  function openEdit(user) {
    setEditForm({
      firstname: user.firstname || "",
      lastname: user.lastname || "",
      bloodType: user.bloodType || "",
      role: user.role || "user",
    });
    setEditError("");
    setEditing(user);
  }

  async function saveEdit(e) {
    e.preventDefault();
    setEditError("");

    if (!editForm.firstname.trim() || !editForm.lastname.trim()) {
      setEditError("First and last name are both required.");
      return;
    }

    setEditSaving(true);
    try {
      await api.put(`/users/updateuserbyid/${editing._id}`, {
        firstname: editForm.firstname.trim(),
        lastname: editForm.lastname.trim(),
        bloodType: editForm.bloodType,
        role: editForm.role,
      });
      setBanner({ kind: "success", text: `Updated ${editing.email}.` });
      setEditing(null);
      await load();
    } catch (err) {
      setEditError(
        err.response?.data?.message || "Could not save those changes."
      );
    } finally {
      setEditSaving(false);
    }
  }

  async function confirmDelete() {
    if (!pendingDelete) return;
    const target = pendingDelete;
    setPendingDelete(null);

    try {
      await api.delete(`/users/deleteuserbyid/${target._id}`);
      setBanner({ kind: "success", text: `Deleted ${target.email}.` });
      await load();
    } catch (err) {
      setBanner({
        kind: "error",
        text: err.response?.data?.message || "Could not delete that account.",
      });
    }
  }

  return (
    <div className="es-page" style={{ paddingTop: "1.5rem" }}>
      <p className="es-eyebrow">Administration</p>
      <h1 className="es-h1">User accounts</h1>
      <p className="es-lede">
        Everyone with an EduSpecial login. Accounts are created through the
        normal registration flow, so new users still receive a verification
        email.
      </p>

      {banner && (
        <div
          className={`es-alert es-alert--${banner.kind === "success" ? "success" : "error"}`}
          role={banner.kind === "success" ? "status" : "alert"}
        >
          {banner.text}
        </div>
      )}

      <div
        style={{
          display: "flex",
          gap: "1rem",
          flexWrap: "wrap",
          alignItems: "flex-end",
          marginBottom: "1.5rem",
        }}
      >
        <div className="es-field" style={{ margin: 0, flex: "1 1 16rem" }}>
          <label className="es-label" htmlFor="user-search">
            Search users
          </label>
          <input
            id="user-search"
            className="es-input"
            type="search"
            placeholder="Name, email or role"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </div>

        <button
          type="button"
          className="es-btn es-btn--primary"
          onClick={() => setShowAddForm((v) => !v)}
          aria-expanded={showAddForm}
        >
          {showAddForm ? "Cancel" : "Add user"}
        </button>

        <button type="button" className="es-btn es-btn--ghost" onClick={load}>
          Refresh
        </button>
      </div>

      {showAddForm && (
        <section className="es-card" style={{ marginBottom: "2rem" }}>
          <div className="es-card__body">
            <h2 className="es-h2">New account</h2>

            {formError && (
              <div className="es-alert es-alert--error" role="alert">
                {formError}
              </div>
            )}

            <form onSubmit={handleAddSubmit}>
              <div
                style={{
                  display: "grid",
                  gap: "0 1rem",
                  gridTemplateColumns: "repeat(auto-fit, minmax(14rem, 1fr))",
                }}
              >
                <div className="es-field">
                  <label className="es-label" htmlFor="nu-first">First name</label>
                  <input id="nu-first" className="es-input" value={form.firstname}
                    onChange={(e) => setForm((f) => ({ ...f, firstname: e.target.value }))} />
                </div>
                <div className="es-field">
                  <label className="es-label" htmlFor="nu-last">Last name</label>
                  <input id="nu-last" className="es-input" value={form.lastname}
                    onChange={(e) => setForm((f) => ({ ...f, lastname: e.target.value }))} />
                </div>
                <div className="es-field">
                  <label className="es-label" htmlFor="nu-email">Email</label>
                  <input id="nu-email" type="email" className="es-input" value={form.email}
                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
                </div>
                <div className="es-field">
                  <label className="es-label" htmlFor="nu-blood">Blood type</label>
                  <select id="nu-blood" className="es-select" value={form.bloodType}
                    onChange={(e) => setForm((f) => ({ ...f, bloodType: e.target.value }))}>
                    {BLOOD_TYPES.map((b) => <option key={b} value={b}>{b}</option>)}
                  </select>
                </div>
                <div className="es-field">
                  <label className="es-label" htmlFor="nu-pw">Password</label>
                  <input id="nu-pw" type="password" className="es-input" value={form.password}
                    autoComplete="new-password"
                    onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} />
                </div>
                <div className="es-field">
                  <label className="es-label" htmlFor="nu-pw2">Confirm password</label>
                  <input id="nu-pw2" type="password" className="es-input" value={form.confirmPassword}
                    autoComplete="new-password"
                    onChange={(e) => setForm((f) => ({ ...f, confirmPassword: e.target.value }))} />
                </div>
              </div>

              <button type="submit" className="es-btn es-btn--primary" disabled={saving}>
                {saving ? "Creating…" : "Create account"}
              </button>
            </form>
          </div>
        </section>
      )}

      {status === "loading" && (
        <div className="es-card" aria-busy="true">
          <div className="es-card__body">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="es-skeleton es-skeleton--text" style={{ width: `${90 - i * 8}%` }} />
            ))}
          </div>
          <span className="es-visually-hidden">Loading users</span>
        </div>
      )}

      {status === "error" && (
        <div className="es-alert es-alert--error" role="alert">
          {loadError}{" "}
          <button type="button" className="es-btn es-btn--ghost es-btn--sm" onClick={load}>
            Retry
          </button>
        </div>
      )}

      {status === "ready" && filtered.length === 0 && (
        <div className="es-state">
          <p className="es-state__title">
            {users.length === 0 ? "No user accounts yet" : "No users match that search"}
          </p>
          <p>{users.length === 0 ? "Accounts will appear here once created." : "Try a different term."}</p>
        </div>
      )}

      {status === "ready" && filtered.length > 0 && (
        <>
          <div className="es-table-wrap">
            <table className="es-table">
              <caption className="es-visually-hidden">EduSpecial user accounts</caption>
              <thead>
                <tr>
                  <th scope="col">Name</th>
                  <th scope="col">Email</th>
                  <th scope="col">Role</th>
                  <th scope="col">Verified</th>
                  <th scope="col">Blood type</th>
                  <th scope="col">Joined</th>
                  <th scope="col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((u) => (
                  <tr key={u._id}>
                    <td>{u.firstname} {u.lastname}</td>
                    <td>{u.email}</td>
                    <td>
                      <span className={`es-badge ${u.role === "admin" ? "es-badge--neutral" : "es-badge--neutral"}`}>
                        {u.role === "admin" ? "Administrator" : "Member"}
                      </span>
                    </td>
                    <td>
                      <span className={`es-badge ${u.verified ? "es-badge--ok" : "es-badge--warn"}`}>
                        {u.verified ? "Verified" : "Pending"}
                      </span>
                    </td>
                    <td>{u.bloodType || "—"}</td>
                    <td>{formatDateTime(u.createdAt)}</td>
                    <td>
                      <div style={{ display: "flex", gap: "0.4rem" }}>
                        <button
                          type="button"
                          className="es-btn es-btn--ghost es-btn--sm"
                          aria-label={`Edit ${u.firstname} ${u.lastname}`}
                          onClick={() => openEdit(u)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="es-btn es-btn--danger es-btn--sm"
                          aria-label={`Delete ${u.firstname} ${u.lastname}`}
                          onClick={() => setPendingDelete(u)}
                          // Deleting the account you are signed in as would
                          // immediately invalidate your own session.
                          disabled={u.email === currentUser?.email}
                          title={u.email === currentUser?.email ? "You cannot delete your own account" : undefined}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ display: "flex", gap: "1rem", alignItems: "center", marginTop: "1rem", flexWrap: "wrap" }}>
            <button type="button" className="es-btn es-btn--ghost es-btn--sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={safePage <= 1}>
              Previous
            </button>
            <span className="es-muted" aria-live="polite">
              Page {safePage} of {totalPages} · {filtered.length} user{filtered.length === 1 ? "" : "s"}
            </span>
            <button type="button" className="es-btn es-btn--ghost es-btn--sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={safePage >= totalPages}>
              Next
            </button>
          </div>
        </>
      )}

      <Dialog
        open={editing !== null}
        onClose={() => setEditing(null)}
        title={editing ? `Edit ${editing.firstname} ${editing.lastname}` : "Edit user"}
      >
        {editError && (
          <div className="es-alert es-alert--error" role="alert">{editError}</div>
        )}

        <form onSubmit={saveEdit}>
          <div className="es-field">
            <label className="es-label" htmlFor="eu-email">Email address</label>
            <input
              id="eu-email"
              className="es-input"
              value={editing?.email || ""}
              readOnly
              aria-describedby="eu-email-hint"
            />
            <p className="es-hint" id="eu-email-hint">
              The address is the sign-in identity and can&rsquo;t be changed here.
            </p>
          </div>

          <div className="es-row">
            <div className="es-field">
              <label className="es-label" htmlFor="eu-first">First name</label>
              <input
                id="eu-first"
                className="es-input"
                value={editForm.firstname}
                onChange={(e) =>
                  setEditForm((f) => ({ ...f, firstname: e.target.value }))
                }
              />
            </div>

            <div className="es-field">
              <label className="es-label" htmlFor="eu-last">Last name</label>
              <input
                id="eu-last"
                className="es-input"
                value={editForm.lastname}
                onChange={(e) =>
                  setEditForm((f) => ({ ...f, lastname: e.target.value }))
                }
              />
            </div>
          </div>

          <div className="es-row">
            <div className="es-field">
              <label className="es-label" htmlFor="eu-blood">Blood type</label>
              <select
                id="eu-blood"
                className="es-select"
                value={editForm.bloodType}
                onChange={(e) =>
                  setEditForm((f) => ({ ...f, bloodType: e.target.value }))
                }
              >
                <option value="">Not specified</option>
                {BLOOD_TYPES.map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>

            <div className="es-field">
              <label className="es-label" htmlFor="eu-role">Role</label>
              <select
                id="eu-role"
                className="es-select"
                value={editForm.role}
                onChange={(e) =>
                  setEditForm((f) => ({ ...f, role: e.target.value }))
                }
                disabled={editing?.email === currentUser?.email}
              >
                <option value="user">Member</option>
                <option value="admin">Administrator</option>
              </select>
              <p className="es-hint">
                {editing?.email === currentUser?.email
                  ? "You can't change your own role."
                  : "Only an administrator can change a role."}
              </p>
            </div>
          </div>

          <p className="es-hint" style={{ marginBottom: "1rem" }}>
            Passwords aren&rsquo;t editable here. Ask the account holder to use
            the &ldquo;Forgot your password?&rdquo; link on the sign-in page.
          </p>

          <div style={{ display: "flex", gap: "0.5rem" }}>
            <button type="submit" className="es-btn es-btn--primary" disabled={editSaving}>
              {editSaving ? "Saving…" : "Save changes"}
            </button>
            <button
              type="button"
              className="es-btn es-btn--ghost"
              onClick={() => setEditing(null)}
            >
              Cancel
            </button>
          </div>
        </form>
      </Dialog>

      {pendingDelete && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="del-title"
          style={{
            position: "fixed", inset: 0, background: "rgb(31 41 51 / 0.45)",
            display: "grid", placeItems: "center", padding: "1rem", zIndex: 50,
          }}
        >
          <div className="es-card" style={{ maxWidth: "26rem", width: "100%" }}>
            <div className="es-card__body">
              <h2 className="es-h2" id="del-title">Delete this account?</h2>
              <p className="es-body">
                <strong>{pendingDelete.firstname} {pendingDelete.lastname}</strong>{" "}
                ({pendingDelete.email}) will be removed permanently. This cannot
                be undone.
              </p>
              <div style={{ display: "flex", gap: "0.5rem", marginTop: "1.5rem" }}>
                <button type="button" className="es-btn es-btn--danger" onClick={confirmDelete}>
                  Delete account
                </button>
                <button type="button" className="es-btn es-btn--ghost" onClick={() => setPendingDelete(null)}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
